import axios from 'axios';
import { merge, omit, forEach, partial } from 'lodash';
import { handleServiceError } from 'utils/errors';

const DEFAULT_CONFIG = {};

const DEFAULT_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json',
};

export const http = config => {
  const headers = merge(DEFAULT_HEADERS, config.headers || {});

  let instance;
  if (config.axiosInstance) {
    instance = config.axiosInstance;
  } else {
    const combinedConfig = merge(DEFAULT_CONFIG, omit(config, ['headers']));
    instance = axios.create(combinedConfig);
  }

  const setHeader = (name, value) => {
    headers[name] = value;

    return instance;
  };

  const setHeaders = values => {
    forEach(values, (value, name) => setHeader(name, value));

    return instance;
  };

  const deleteHeader = name => {
    delete headers[name];

    return instance;
  };

  const setBaseURL = newURL => {
    instance.defaults.baseURL = newURL;

    return instance;
  };

  const getBaseURL = () => {
    return instance.defaults.baseURL;
  };

  const doRequest = async axiosRequestConfig => {
    const requestConfig = {
      ...axiosRequestConfig,
      headers: {
        ...headers,
        ...axiosRequestConfig.headers,
      },
    };

    return instance.request(requestConfig).catch(err => handleServiceError(err));
  };

  /**
   * Make the request for GET, HEAD, DELETE
   */
  const doRequestWithoutBody = (method, url, params = {}, axiosConfig = {}) => {
    return doRequest(merge({ url, params, method }, axiosConfig));
  };

  /**
   * Make the request for POST, PUT, PATCH
   */
  const doRequestWithBody = (method, url, data = null, axiosConfig = {}) => {
    return doRequest(merge({ url, method, data }, axiosConfig));
  };

  const methods = {
    axiosInstance: instance,
    setHeader,
    setHeaders,
    deleteHeader,
    headers,
    setBaseURL,
    getBaseURL,
    get: partial(doRequestWithoutBody, 'get'),
    delete: partial(doRequestWithoutBody, 'delete'),
    head: partial(doRequestWithoutBody, 'head'),
    post: partial(doRequestWithBody, 'post'),
    put: partial(doRequestWithBody, 'put'),
    patch: partial(doRequestWithBody, 'patch'),
    link: partial(doRequestWithoutBody, 'link'),
    unlink: partial(doRequestWithoutBody, 'unlink'),
  };

  return methods;
};

export default http;
